// Sample HVAC diagnostic flow + mock data for Guru chatbot

export const sampleFlow = {
  name: "HVAC \u2014 AC Not Cooling",
  nodes: [
    { id: "n1", type: "startNode", position: { x: 280, y: 20 }, data: { label: "Start Diagnosis", description: "Begin AC not cooling troubleshooting" } },
    { id: "n2", type: "questionNode", position: { x: 240, y: 140 }, data: { label: "Is the thermostat set to COOL?", choices: ["Yes", "No"] } },
    { id: "n3", type: "actionNode", position: { x: 40, y: 280 }, data: { label: "Set thermostat to COOL", description: "Set mode to COOL and target temp below room temp" } },
    { id: "n4", type: "questionNode", position: { x: 440, y: 280 }, data: { label: "Is the outdoor unit running?", choices: ["Yes", "No"] } },
    { id: "n5", type: "questionNode", position: { x: 640, y: 420 }, data: { label: "Are the coils visibly iced over?", choices: ["Yes", "No"] } },
    { id: "n6", type: "questionNode", position: { x: 220, y: 420 }, data: { label: "Check breaker \u2014 is it tripped?", choices: ["Yes", "No"] } },
    { id: "n7", type: "diagnosisNode", position: { x: 760, y: 560 }, data: { label: "Likely low refrigerant", recommendation: "Inspect for refrigerant leak. Recover, repair, recharge. Estimated 2\u20133 hrs labor." } },
    { id: "n8", type: "diagnosisNode", position: { x: 480, y: 560 }, data: { label: "Possible bad capacitor", recommendation: "Test dual-run capacitor. Replace if reading is >10% off rated \u00b5F. ~$280 part + labor." } },
    { id: "n9", type: "diagnosisNode", position: { x: 60, y: 560 }, data: { label: "Electrical fault", recommendation: "Reset breaker. If trips again, inspect compressor / contactor for short. Schedule electrical inspection." } },
    { id: "n10", type: "diagnosisNode", position: { x: 240, y: 560 }, data: { label: "Check airflow", recommendation: "Replace air filter, inspect blower motor, verify return grilles unobstructed." } },
  ],
  edges: [
    { id: "e1", source: "n1", target: "n2", animated: true },
    { id: "e2", source: "n2", sourceHandle: "No", target: "n3", label: "No" },
    { id: "e3", source: "n2", sourceHandle: "Yes", target: "n4", label: "Yes" },
    { id: "e4", source: "n4", sourceHandle: "No", target: "n6", label: "No" },
    { id: "e5", source: "n4", sourceHandle: "Yes", target: "n5", label: "Yes" },
    { id: "e6", source: "n5", sourceHandle: "Yes", target: "n7", label: "Yes" },
    { id: "e7", source: "n5", sourceHandle: "No", target: "n8", label: "No" },
    { id: "e8", source: "n6", sourceHandle: "Yes", target: "n9", label: "Yes" },
    { id: "e9", source: "n6", sourceHandle: "No", target: "n10", label: "No" },
  ],
};

export const trainedFiles = [
  { id: "f1", name: "Carrier_HVAC_Service_Manual.pdf", size: "4.2 MB", date: "2025-06-22", type: "pdf", status: "Indexed" },
  { id: "f2", name: "Refrigerant_Charging_Guide_R410A.pdf", size: "1.8 MB", date: "2025-06-22", type: "pdf", status: "Indexed" },
  { id: "f3", name: "Trane_XR16_Troubleshooting.pdf", size: "2.6 MB", date: "2025-06-25", type: "pdf", status: "Indexed" },
  { id: "f4", name: "Capacitor_Replacement_Steps.docx", size: "412 KB", date: "2025-06-28", type: "doc", status: "Indexed" },
  { id: "f5", name: "Common_Plumbing_Failures.pdf", size: "3.1 MB", date: "2025-07-01", type: "pdf", status: "Indexed" },
  { id: "f6", name: "Electrical_Panel_Inspection_Checklist.pdf", size: "880 KB", date: "2025-07-03", type: "pdf", status: "Indexed" },
];

export const mockReplies = [
  "Based on the Carrier service manual, when an AC unit fails to cool, you should first verify the thermostat mode and temperature differential. The most common causes are: dirty air filter, refrigerant undercharge, failed dual-run capacitor, or iced evaporator coil.",
  "From the Trane XR16 troubleshooting guide: a humming outdoor unit that won't start is almost always a failed start capacitor. Check microfarad reading with a multimeter \u2014 a reading below 90% of rated value indicates failure.",
  "Refrigerant leaks commonly occur at flare connections, schrader valves, and the indoor evaporator coil. Use an electronic leak detector or UV dye for accurate detection. Per EPA Section 608, leaks above 1.5 lb/yr in commercial units must be repaired.",
  "If the breaker trips immediately on reset, do NOT keep resetting it. This indicates a hard short \u2014 likely the compressor windings or contactor. Disconnect power and inspect terminals for burn marks. Megger test the compressor before replacement.",
  "Iced coils with the outdoor unit running typically means: (1) low refrigerant charge, (2) restricted airflow across the evaporator, or (3) a stuck TXV / metering device. Let the system thaw fully (4\u20136 hrs) before re-testing.",
  "Quarterly maintenance per the indexed manual: replace filters, clean condenser coils, check refrigerant pressures, tighten electrical connections, lubricate motor bearings, verify thermostat calibration, inspect ductwork for leaks.",
];

export const suggestedPrompts = [
  "Why is the compressor humming but not starting?",
  "What's the proper R-410A charging procedure?",
  "How do I test a dual-run capacitor?",
  "Customer reports water leaking from indoor unit \u2014 what's the cause?",
];

// Mock image-analysis scenarios. AI "vision" cycles through these.
export const visionScenarios = [
  {
    title: "HVAC Manifold Gauge Set \u2014 R-410A",
    readings: [
      { label: "Low-side pressure", value: "32 psi", flag: "low" },
      { label: "High-side pressure", value: "248 psi", flag: "low" },
      { label: "Suction line temp", value: "62 \u00b0F", flag: "ok" },
      { label: "Liquid line temp", value: "92 \u00b0F", flag: "ok" },
      { label: "Ambient temp", value: "78 \u00b0F", flag: "ok" },
    ],
    interpretation:
      "Low-side reads 32 psi (target 65\u201375 psi) and high-side reads 248 psi (target 380\u2013400 psi at this ambient). Both pressures are significantly under-charged. Pattern is consistent with a refrigerant leak per the Carrier service manual \u00a7 4.3.",
    likelyDiagnosis: "Low refrigerant charge / refrigerant leak",
  },
  {
    title: "Dual-Run Capacitor \u2014 Multimeter Reading",
    readings: [
      { label: "Rated capacitance", value: "45 / 5 \u00b5F", flag: "ok" },
      { label: "Measured \u2014 herm", value: "32.1 \u00b5F", flag: "low" },
      { label: "Measured \u2014 fan", value: "4.1 \u00b5F", flag: "low" },
      { label: "Tolerance band", value: "\u00b1 6%", flag: "ok" },
      { label: "Visible bulging", value: "Yes (top dome)", flag: "alert" },
    ],
    interpretation:
      "Herm leg reads 28.7% below rated value; fan leg 18% below. EPA/AHRI guidance: replace if any leg is more than 10% off. Top dome bulge indicates pressure venting \u2014 a hard failure is imminent.",
    likelyDiagnosis: "Failed dual-run capacitor",
  },
  {
    title: "Electrical Panel \u2014 Breaker Inspection",
    readings: [
      { label: "Main breaker", value: "200 A \u2014 ON", flag: "ok" },
      { label: "AC condenser breaker", value: "30 A \u2014 TRIPPED", flag: "alert" },
      { label: "Air handler breaker", value: "15 A \u2014 ON", flag: "ok" },
      { label: "Discoloration / scorch", value: "Slight on contactor lead", flag: "alert" },
      { label: "Wire gauge", value: "10 AWG copper", flag: "ok" },
    ],
    interpretation:
      "Condenser breaker is tripped with mild scorching observed on the contactor lead. Likely a short on the load side \u2014 do NOT reset until the compressor and contactor are megger-tested.",
    likelyDiagnosis: "Hard short \u2014 compressor or contactor fault",
  },
  {
    title: "Indoor Coil \u2014 Visual Inspection",
    readings: [
      { label: "Ice formation", value: "Heavy on suction header", flag: "alert" },
      { label: "Filter condition", value: "Clogged \u2014 MERV-11 saturated", flag: "alert" },
      { label: "Blower amp draw", value: "5.8 A (rated 6.2 A)", flag: "ok" },
      { label: "Drain pan", value: "Standing water", flag: "alert" },
      { label: "TXV bulb contact", value: "Secure", flag: "ok" },
    ],
    interpretation:
      "Iced evaporator with a saturated filter and standing water in the drain pan. Most common cause is restricted airflow leading to coil temp drop below freezing. Replace filter, thaw the system, and re-test pressures before proceeding.",
    likelyDiagnosis: "Iced evaporator \u2014 airflow restriction",
  },
];
